/**
 * Guía del profesor (AUTONOMY · Layer 3).
 */
import { msg } from "@lingui/core/macro";
import { GraduationCap } from "lucide-solid";

import type { HelpGuide } from "../types";
import { Bullets, GoTo, Note, Steps } from "../components/help-prose";

export const teacherGuide: HelpGuide = {
  key: "help.teacher",
  slug: "profesor",
  title: msg`Teacher guide`,
  description: msg`Run your cohorts day to day: attendance, gradebook, assignments, live classes, forum and your public profile.`,
  audience: msg`Teachers`,
  icon: GraduationCap,
  sections: [
    {
      id: "mis-cohortes",
      title: msg`Your cohorts`,
      summary: msg`Each cohort you teach has one page with everything: roster, schedule, attendance, gradebook and forum tabs.`,
      body: () => (
        <>
          <Steps
            items={[
              msg`Open Academy → Cohorts to see the cohorts assigned to you.`,
              msg`The roster tab shows enrolled students, waitlist and withdrawals.`,
              msg`The schedule tab shows the weekly slots and generated class sessions.`,
            ]}
          />
          <GoTo to="/academy/cohorts" label={msg`Open Cohorts`} />
        </>
      ),
    },
    {
      id: "asistencia",
      title: msg`Attendance`,
      summary: msg`Take attendance per session with roll call, or let students self-check-in with the rotating code or QR of the session.`,
      body: () => (
        <>
          <Bullets
            items={[
              msg`Roll call: mark each student present, late, absent or excused from the session sheet — it works offline and syncs later.`,
              msg`Self check-in: share the session code or QR; students confirm from their phone in Check-in.`,
              msg`Consecutive absences beyond the academy threshold open a risk alert visible to admins.`,
            ]}
          />
          <GoTo to="/schedule" label={msg`Open Schedule`} />
        </>
      ),
    },
    {
      id: "evaluacion",
      title: msg`Assignments and gradebook`,
      summary: msg`Create assignments with rubrics, grade submissions, and manage the weighted gradebook that produces the final grade.`,
      body: () => (
        <>
          <Bullets
            items={[
              msg`Assignments live inside course units as modules; students submit files or text.`,
              msg`The gradebook combines categories with weights (e.g. Assignments 40 / Exams 40 / Participation 20) from assignment grades, interactive block scores, attendance or manual items.`,
              msg`Grades follow the cohort's grading scale (Venezuela 0–20 by default) — the scale is configured by the academy, per course or per cohort.`,
              msg`Closing the cohort seals final grades and issues certificates automatically to those who pass.`,
            ]}
          />
          <GoTo to="/academy/cohorts" label={msg`Open your cohort's gradebook`} />
        </>
      ),
    },
    {
      id: "clases-en-vivo",
      title: msg`Live classes and recordings`,
      summary: msg`Live sessions open inside the app (Jitsi) or through an external link (Zoom, Meet, custom), and their recordings can be re-watched later.`,
      body: () => (
        <>
          <Steps
            items={[
              msg`The meeting provider is configured per cohort (defaults) or per session (override) in the cohort's schedule tab.`,
              msg`Students join from the session page only within the session time window.`,
              msg`After class, upload the recording and link it to the session: enrolled students re-watch it with the built-in player, resuming where they left off.`,
            ]}
          />
          <GoTo to="/schedule" label={msg`Open Schedule`} />
        </>
      ),
    },
    {
      id: "plan-de-clase",
      title: msg`Class plan (flipped classroom)`,
      summary: msg`Turn each class into a before/during/after path: pick course modules and tag them pre, live or post. Students see a clear week-by-week route with completion ticks.`,
      body: () => (
        <>
          <Steps
            items={[
              msg`In the cohort's Schedule tab, open a session and press "Plan".`,
              msg`Add course modules and tag each as Before (prep video/reading), During (the live class) or After (the challenge).`,
              msg`Change a module's phase or remove it anytime; removals go to the trash and can be undone.`,
            ]}
          />
          <Note
            msg={msg`Requires the "Flipped classroom" feature enabled for your academy. Everything students see comes from synced data, so it works offline.`}
          />
          <GoTo to="/schedule" label={msg`Open Schedule`} />
        </>
      ),
    },
    {
      id: "comunicacion",
      title: msg`Announcements and forum`,
      summary: msg`Publish announcements to your cohort and moderate the course forum; students get in-app notifications.`,
      body: () => (
        <>
          <Bullets
            items={[
              msg`Announcements can target the whole academy, one campus or one cohort, and can be scheduled for a future date.`,
              msg`Forum threads belong to course modules; teachers can pin and moderate posts.`,
            ]}
          />
          <GoTo to="/academy/announcements" label={msg`Open Announcements`} />
        </>
      ),
    },
    {
      id: "perfil-publico",
      title: msg`Your public teacher page`,
      summary: msg`Publish a public profile page with your bio, specialties and photo — useful for the academy's marketing.`,
      body: () => (
        <>
          <Steps
            items={[
              msg`Open your teacher profile editor (from your profile or via an admin).`,
              msg`Fill title, bio and specialties, then enable "Public page".`,
              msg`Share the public URL — it works without signing in.`,
            ]}
          />
          <Note msg={msg`Only you or an academy admin can enable or edit your public page.`} />
          <GoTo to="/profile" label={msg`Open your profile`} />
        </>
      ),
    },
    {
      id: "tutorias",
      title: msg`Tutoring (for tutors)`,
      summary: msg`If you are a tutor of a cohort, review submissions from your correction queue: return them with feedback or approve them, tracked against an SLA.`,
      body: () => (
        <>
          <Bullets
            items={[
              msg`Your correction queue lists submissions pending review across your cohorts, oldest first.`,
              msg`Sort by due date (SLA) to tackle the ones about to breach the target; overdue ones are flagged.`,
              msg`Open a submission to see its content, the rubric and the full version history before deciding.`,
              msg`Return it with feedback (the student can resubmit), approve it, or reject it — the student is notified.`,
            ]}
          />
          <Note
            msg={msg`A tutor can review and grade submissions of their cohorts but does not manage the cohort structure (enrollments, teachers) — that stays with the lead teacher and admins.`}
          />
          <GoTo to="/academy/tutoring" label={msg`Open Tutoring`} />
        </>
      ),
    },
  ],
};
